/**
 * Mercado Sectorial — "Commercial Cockpit" (Iter277)
 *
 * Inteligencia de mercado sectorial de residuos (España + Australia), importada
 * de MARKET 2026.xlsx: licitaciones detectadas en todo el sector (propias y de
 * terceros), aperturas de sobres, requisitos de solvencia, ranking de empresas
 * competidoras (LICITADAS PUNTOS) y solicitudes de estructura de costes.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Globe2, TrendingUp, Trophy, Building2, FileSearch, Receipt, Upload,
  Loader2, Search, ExternalLink, ChevronDown, ChevronUp,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtM = (n) => (n == null ? "—" : `${(Number(n) / 1_000_000).toFixed(1)} M€`);
const fmtEur = (n) =>
  n == null || typeof n !== "number" ? "—" :
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);

export default function MercadoSectorial() {
  const { user } = useAuth();
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);

  const loadKpis = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/market-intel/kpis`);
      setKpis(r.data);
    } catch { toast.error("No se pudieron cargar los KPIs de mercado"); }
    setLoading(false);
  }, []);

  useEffect(() => { loadKpis(); }, [loadKpis]);

  const canImport = user?.role === "admin" || user?.role === "offer_manager";

  const handleImport = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/market-intel/import`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Mercado reimportado: ${r.data.counts.market_intel_tenders} licitaciones`);
      await loadKpis();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error reimportando el Excel de mercado"); }
    setImporting(false);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="mercado-sectorial-page">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Globe2 className="text-[#007934]" size={26} />
            Mercado Sectorial
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Inteligencia de mercado de residuos — España + Australia · histórico 2025-2026, aperturas, solvencias y competidores.
          </p>
        </div>
        {canImport && (
          <label className="text-xs bg-white border border-slate-300 hover:border-[#007934] text-slate-700 px-3 py-2 rounded-lg flex items-center gap-2 cursor-pointer font-semibold"
            data-testid="mercado-sectorial-import-label">
            {importing ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            Reimportar Excel MARKET
            <input type="file" accept=".xlsx" className="hidden" disabled={importing}
              data-testid="mercado-sectorial-import-input"
              onChange={(e) => handleImport(e.target.files?.[0])} />
          </label>
        )}
      </div>

      {loading ? (
        <div className="text-center py-20 text-slate-400"><Loader2 className="animate-spin mx-auto mb-2" size={28} />Cargando...</div>
      ) : (
        <Tabs defaultValue="resumen" className="w-full">
          <TabsList className="mb-4 flex-wrap h-auto">
            <TabsTrigger value="resumen" data-testid="tab-resumen">Resumen</TabsTrigger>
            <TabsTrigger value="licitaciones" data-testid="tab-licitaciones">Licitaciones</TabsTrigger>
            <TabsTrigger value="aperturas" data-testid="tab-aperturas">Aperturas</TabsTrigger>
            <TabsTrigger value="competidores" data-testid="tab-competidores">Competidores</TabsTrigger>
            <TabsTrigger value="solvencias" data-testid="tab-solvencias">Solvencias</TabsTrigger>
            <TabsTrigger value="costes" data-testid="tab-costes">Est. Costes</TabsTrigger>
          </TabsList>

          <TabsContent value="resumen"><ResumenTab kpis={kpis} /></TabsContent>
          <TabsContent value="licitaciones"><LicitacionesTab /></TabsContent>
          <TabsContent value="aperturas"><AperturasTab /></TabsContent>
          <TabsContent value="competidores"><CompetidoresTab /></TabsContent>
          <TabsContent value="solvencias"><SolvenciasTab /></TabsContent>
          <TabsContent value="costes"><CostesTab /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, sub }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center gap-2 text-slate-400 text-xs font-semibold uppercase tracking-wide mb-1">
        {Icon && <Icon size={13} />} {label}
      </div>
      <p className="text-2xl font-bold text-slate-800">{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function ResumenTab({ kpis }) {
  if (!kpis) return null;
  const chartData = (kpis.por_anio || []).map((d) => ({ anio: String(d.anio), n: d.n, pbl_mm: Math.round(d.pbl_total / 1e6) }));
  return (
    <div className="space-y-6" data-testid="mercado-resumen-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Licitaciones detectadas" value={kpis.total_licitaciones} icon={Globe2} />
        <KpiCard label="PBL total mercado" value={fmtM(kpis.pbl_total_mercado)} icon={TrendingUp} />
        <KpiCard label="VSM presentó (con importe)" value={kpis.vsm_presento_con_importe} icon={Trophy} />
        <KpiCard label="Solvencias registradas" value={kpis.n_solvencias} icon={FileSearch} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">PBL del mercado por año (M€)</h3>
          <div style={{ width: "100%", minWidth: 280, height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="anio" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <RTooltip formatter={(v) => `${v} M€`} />
                <Bar dataKey="pbl_mm" fill="#007934" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Licitaciones por región</h3>
          <div className="space-y-2">
            {Object.entries(kpis.por_region || {}).map(([region, d]) => (
              <div key={region} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2">
                <span className="font-semibold text-slate-700">{region}</span>
                <span className="text-slate-500">{d.n} licitaciones · {fmtM(d.pbl_total)}</span>
              </div>
            ))}
          </div>
          <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mt-4 mb-2">Por tipo de contrato</h3>
          <div className="flex gap-2 flex-wrap">
            {Object.entries(kpis.por_tipo_contrato || {}).map(([tipo, n]) => (
              <span key={tipo} className="text-xs bg-slate-100 text-slate-700 px-2.5 py-1 rounded-full font-semibold">{tipo}: {n}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function LicitacionesTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [anio, setAnio] = useState("");
  const [region, setRegion] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 50 };
      if (q) params.q = q;
      if (anio) params.anio = anio;
      if (region) params.region = region;
      const r = await axios.get(`${API}/market-intel/tenders`, { params });
      setItems(r.data.items); setTotal(r.data.total);
    } catch { toast.error("Error cargando licitaciones"); }
    setLoading(false);
  }, [q, anio, region]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="mercado-licitaciones-tab">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar cliente / alcance..."
            className="pl-8 w-64 h-9 text-sm" data-testid="mercado-licitaciones-search" />
        </div>
        <select value={anio} onChange={(e) => setAnio(e.target.value)} className="h-9 text-sm border border-slate-300 rounded-md px-2" data-testid="mercado-licitaciones-anio-select">
          <option value="">Todos los años</option>
          <option value="2026">2026</option>
          <option value="2025">2025</option>
        </select>
        <select value={region} onChange={(e) => setRegion(e.target.value)} className="h-9 text-sm border border-slate-300 rounded-md px-2" data-testid="mercado-licitaciones-region-select">
          <option value="">Todas las regiones</option>
          <option value="ESPAÑA">España</option>
          <option value="AUSTRALIA">Australia</option>
        </select>
        <span className="text-xs text-slate-400">{total} resultados</span>
      </div>
      {loading ? <Loader2 className="animate-spin text-slate-400 mx-auto my-8" /> : (
        <div className="overflow-x-auto bg-white border border-slate-200 rounded-xl">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr>
                <th className="text-left px-3 py-2">Cliente</th>
                <th className="text-left px-3 py-2">Alcance</th>
                <th className="text-right px-3 py-2">PBL</th>
                <th className="text-left px-3 py-2">Duración</th>
                <th className="text-left px-3 py-2">Tipo</th>
                <th className="text-left px-3 py-2">Publicación</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`mercado-tender-row-${i}`}>
                  <td className="px-3 py-2 font-medium text-slate-700 max-w-[200px] truncate" title={it.cliente}>{it.cliente}</td>
                  <td className="px-3 py-2 text-slate-500 max-w-[320px] truncate" title={it.alcance}>{it.alcance}</td>
                  <td className="px-3 py-2 text-right font-semibold text-slate-700">{fmtEur(it.pbl)}</td>
                  <td className="px-3 py-2 text-slate-500">{it.duracion || "—"}</td>
                  <td className="px-3 py-2"><span className="text-xs bg-slate-100 px-2 py-0.5 rounded-full">{it.conc_om || "—"}</span></td>
                  <td className="px-3 py-2 text-slate-500">{it.fecha_publicacion ? String(it.fecha_publicacion).slice(0, 10) : "—"}</td>
                  <td className="px-3 py-2">
                    {it.link_url && <a href={it.link_url} target="_blank" rel="noreferrer"><ExternalLink size={13} className="text-[#007934]" /></a>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AperturasTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [soloGanador, setSoloGanador] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/market-intel/aperturas`, { params: { limit: 50, solo_con_ganador: soloGanador } })
      .then((r) => setItems(r.data.items))
      .catch(() => toast.error("Error cargando aperturas"))
      .finally(() => setLoading(false));
  }, [soloGanador]);

  return (
    <div data-testid="mercado-aperturas-tab">
      <label className="flex items-center gap-2 text-sm text-slate-600 mb-3 cursor-pointer">
        <input type="checkbox" checked={soloGanador} onChange={(e) => setSoloGanador(e.target.checked)} data-testid="mercado-aperturas-solo-ganador" />
        Solo con adjudicatario conocido
      </label>
      {loading ? <Loader2 className="animate-spin text-slate-400 mx-auto my-8" /> : (
        <div className="space-y-3">
          {items.map((it, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`mercado-apertura-row-${i}`}>
              <p className="font-semibold text-slate-800 text-sm">{it.cliente}</p>
              <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{it.alcance}</p>
              {it.adjudicado && (
                <p className="text-xs mt-2"><span className="font-semibold text-[#007934]">Adjudicatario:</span> <span className="text-slate-600 whitespace-pre-line">{it.adjudicado}</span></p>
              )}
              {it.adjudicado2 && <p className="text-xs text-slate-500 whitespace-pre-line mt-1">{it.adjudicado2}</p>}
            </div>
          ))}
          {items.length === 0 && <p className="text-sm text-slate-400 text-center py-8">Sin resultados</p>}
        </div>
      )}
    </div>
  );
}

function CompetidoresTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/market-intel/competidores`)
      .then((r) => setItems(r.data.items))
      .catch(() => toast.error("Error cargando competidores"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loader2 className="animate-spin text-slate-400 mx-auto my-8" />;
  return (
    <div className="overflow-x-auto bg-white border border-slate-200 rounded-xl" data-testid="mercado-competidores-tab">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
          <tr>
            <th className="text-left px-3 py-2">Empresa</th>
            <th className="text-right px-3 py-2">Participaciones</th>
            <th className="text-right px-3 py-2">Pts. totales (media)</th>
            <th className="text-right px-3 py-2">Suma presupuesto ofertado</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, i) => (
            <tr key={i} className={`border-t border-slate-100 ${it.es_vsm ? "bg-emerald-50" : ""}`} data-testid={`mercado-competidor-row-${i}`}>
              <td className="px-3 py-2 font-semibold flex items-center gap-1.5">
                {it.es_vsm && <Trophy size={12} className="text-[#007934]" />} {it.empresa}
              </td>
              <td className="px-3 py-2 text-right">{it.n_participaciones}</td>
              <td className="px-3 py-2 text-right">{it.pts_total_promedio ?? "—"}</td>
              <td className="px-3 py-2 text-right">{fmtEur(it.suma_presupuesto)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SolvenciasTab() {
  const [cliente, setCliente] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const search = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/market-intel/solvencias`, { params: cliente ? { cliente } : {} });
      setItems(r.data.items);
    } catch { toast.error("Error buscando solvencias"); }
    setLoading(false);
  }, [cliente]);

  useEffect(() => { search(); }, [search]);

  return (
    <div data-testid="mercado-solvencias-tab">
      <div className="relative mb-3 w-72">
        <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
        <Input value={cliente} onChange={(e) => setCliente(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()}
          placeholder="Buscar por cliente/organismo..." className="pl-8 h-9 text-sm" data-testid="mercado-solvencias-search" />
      </div>
      {loading ? <Loader2 className="animate-spin text-slate-400 mx-auto my-8" /> : (
        <div className="space-y-2">
          {items.map((it, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-xl p-3" data-testid={`mercado-solvencia-row-${i}`}>
              <button className="w-full flex items-center justify-between text-left" onClick={() => setExpanded(expanded === i ? null : i)}>
                <div>
                  <p className="font-semibold text-sm text-slate-800">{it.cliente}</p>
                  <p className="text-xs text-slate-500 line-clamp-1">{it.alcance}</p>
                </div>
                {expanded === i ? <ChevronUp size={16} className="text-slate-400 shrink-0" /> : <ChevronDown size={16} className="text-slate-400 shrink-0" />}
              </button>
              {expanded === i && (
                <p className="text-xs text-slate-600 whitespace-pre-line mt-3 pt-3 border-t border-slate-100">{it.solvencia_texto || "Sin texto de solvencia registrado."}</p>
              )}
            </div>
          ))}
          {items.length === 0 && <p className="text-sm text-slate-400 text-center py-8">Sin resultados</p>}
        </div>
      )}
    </div>
  );
}

function CostesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/market-intel/costes`)
      .then((r) => setItems(r.data.items))
      .catch(() => toast.error("Error cargando estructura de costes"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loader2 className="animate-spin text-slate-400 mx-auto my-8" />;
  return (
    <div className="space-y-3" data-testid="mercado-costes-tab">
      {items.map((it, i) => (
        <div key={i} className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`mercado-coste-row-${i}`}>
          <div className="flex items-center justify-between">
            <p className="font-semibold text-sm text-slate-800 flex items-center gap-1.5"><Receipt size={13} className="text-[#007934]" /> {it.cliente}</p>
            <span className="text-xs text-slate-400">{it.fecha_solicitud ? String(it.fecha_solicitud).slice(0, 10) : "—"}</span>
          </div>
          <p className="text-xs text-slate-500 mt-2 line-clamp-3">{it.descripcion}</p>
        </div>
      ))}
      {items.length === 0 && <p className="text-sm text-slate-400 text-center py-8">Sin resultados</p>}
    </div>
  );
}
